import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  embedPlaceholder,
  parseVideoUrl,
  providerLabel,
} from '../utils/videoEmbed';
import {
  clearPendingDocVideoFile,
  hasPendingDocVideoFile,
  retryDocVideoUpload,
  startDocVideoUpload,
} from '../utils/docVideoUploadBridge';
import {
  createLocalVideoBlock,
  createVideoEmbedBlock,
  pickDocVideoFile,
} from '../utils/videoFiles';
import { DocVideoPlayer } from '../components/DocVideoPlayer';

const MIN_WIDTH_PCT = 30;
const MAX_WIDTH_PCT = 100;

const revokeBlobUrl = (url) => {
  if (url?.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
};

/**
 * VideoBlock — embed (YouTube / Vimeo / Loom) or uploaded MP4/WebM.
 * Width is stored as props.widthPercent (30–100); embeds keep a 16:9 frame.
 */
export const VideoBlock = ({
  block,
  onUpdate,
  onDelete,
  selectedBlockId,
  dragListeners,
  dragAttributes,
}) => {
  const props = block.props || {};
  const {
    mode = 'chooser',
    provider = null,
    embedUrl,
    originalUrl,
    publicUrl,
    localPreviewUrl,
    fileName,
    uploadStatus,
    uploadProgress,
    errorMessage,
    widthPercent,
  } = props;

  const selected = selectedBlockId === block.id;
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const inputRef = useRef(null);
  const draftWidthRef = useRef(null);
  const [draftWidth, setDraftWidth] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [urlError, setUrlError] = useState('');

  const widthPct =
    draftWidth ?? (typeof widthPercent === 'number' ? widthPercent : 100);

  const playSrc = publicUrl || localPreviewUrl;
  const isUploading =
    uploadStatus === 'uploading' || (uploadStatus === 'local' && !!localPreviewUrl);
  const uploadFailed = mode === 'upload' && uploadStatus === 'error' && !!playSrc;
  const hardError =
    mode === 'upload' && uploadStatus === 'error' && !playSrc && !embedUrl;
  const canRetry = hasPendingDocVideoFile(block.id);

  useEffect(() => {
    if (mode === 'embed' && !embedUrl) {
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [mode, embedUrl, block.id]);

  const commitWidth = useCallback(
    (pct) => {
      const clamped = Math.round(Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, pct)));
      onUpdate?.(block.id, {
        props: { ...block.props, widthPercent: clamped },
      });
    },
    [block.id, block.props, onUpdate]
  );

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const { side, containerLeft, containerWidth, frameRight } = drag;
      if (!containerWidth) return;
      let pct;
      if (side === 'right') {
        pct = ((e.clientX - containerLeft) / containerWidth) * 100;
      } else {
        pct = ((frameRight - e.clientX) / containerWidth) * 100;
      }
      const next = Math.min(MAX_WIDTH_PCT, Math.max(MIN_WIDTH_PCT, pct));
      draftWidthRef.current = next;
      setDraftWidth(next);
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.classList.remove('doc-image-resizing');
      const current = draftWidthRef.current;
      draftWidthRef.current = null;
      setDraftWidth(null);
      if (current != null) commitWidth(current);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [commitWidth]);

  const startResize = (side) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const frame = frameRef.current;
    if (!frame) return;
    const fRect = frame.getBoundingClientRect();
    const wrapper = frame.closest('.block-wrapper') || frame.parentElement;
    const wRect = wrapper.getBoundingClientRect();
    if (!wRect.width) return;
    dragRef.current = {
      side,
      containerLeft: wRect.left,
      containerWidth: wRect.width,
      frameRight: fRect.right,
    };
    document.body.classList.add('doc-image-resizing');
    draftWidthRef.current = widthPct;
    setDraftWidth(widthPct);
  };

  const patchProps = (nextProps) => {
    onUpdate?.(block.id, { props: { ...block.props, ...nextProps } });
  };

  const handleRemove = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    clearPendingDocVideoFile(block.id);
    revokeBlobUrl(localPreviewUrl);
    onDelete?.(block.id);
  };

  const handleRetry = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!retryDocVideoUpload(block.id)) return;
  };

  const chooseEmbed = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setUrlDraft('');
    setUrlError('');
    patchProps({ mode: 'embed', provider: provider || null });
  };

  const chooseUpload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = await pickDocVideoFile();
    if (!file) return;
    revokeBlobUrl(localPreviewUrl);
    clearPendingDocVideoFile(block.id);
    const next = createLocalVideoBlock(file);
    onUpdate?.(block.id, {
      type: 'video',
      content: '',
      props: { ...next.props, widthPercent: widthPercent ?? 100 },
      children: [],
    });
    if (next.props?.uploadStatus === 'local') {
      startDocVideoUpload(block.id, file);
    }
  };

  const submitEmbedUrl = (raw) => {
    const parsed = parseVideoUrl(raw);
    if (!parsed) {
      setUrlError('Use a YouTube, Vimeo, or Loom link');
      return;
    }
    const next = createVideoEmbedBlock(parsed);
    onUpdate?.(block.id, {
      type: 'video',
      content: '',
      props: { ...next.props, widthPercent: widthPercent ?? 100 },
      children: [],
    });
    setUrlError('');
  };

  const onEmbedKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      submitEmbedUrl(urlDraft);
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (embedUrl) return;
      patchProps({ mode: 'chooser', provider: null });
      setUrlDraft('');
      setUrlError('');
    }
  };

  const stopPinClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // —— Chooser ——
  if (mode === 'chooser') {
    return (
      <div className="video-block video-block--chooser" contentEditable={false}>
        <div className="video-block__chooser-title">Add a video</div>
        <div className="video-block__chooser-actions">
          <button
            type="button"
            className="video-block__action video-block__action--primary"
            onClick={chooseEmbed}
          >
            Embed link
          </button>
          <button
            type="button"
            className="video-block__action"
            onClick={chooseUpload}
          >
            Upload
          </button>
          <button type="button" className="video-block__action" onClick={handleRemove}>
            Remove
          </button>
        </div>
        <div className="video-block__hint">
          Embed YouTube, Vimeo, or Loom — or upload MP4/WebM up to 100MB (counts
          toward your file storage)
        </div>
      </div>
    );
  }

  // —— Embed URL prompt ——
  if (mode === 'embed' && !embedUrl) {
    return (
      <div className="video-block video-block--embed-form" contentEditable={false}>
        <div className="video-block__chooser-title">
          {provider ? `Embed ${providerLabel(provider)}` : 'Embed video'}
        </div>
        <div className="video-block__embed-row">
          <input
            ref={inputRef}
            className="video-block__url-input"
            type="url"
            value={urlDraft}
            placeholder={embedPlaceholder(provider)}
            onChange={(e) => {
              setUrlDraft(e.target.value);
              if (urlError) setUrlError('');
            }}
            onKeyDown={onEmbedKeyDown}
            onPaste={(e) => {
              const text = e.clipboardData?.getData('text/plain') || '';
              if (!parseVideoUrl(text.trim())) return;
              e.preventDefault();
              submitEmbedUrl(text.trim());
            }}
          />
          <button
            type="button"
            className="video-block__action video-block__action--primary"
            onClick={() => submitEmbedUrl(urlDraft)}
          >
            Embed
          </button>
        </div>
        {urlError ? <div className="video-block__error-msg">{urlError}</div> : null}
        <div className="video-block__chooser-actions">
          <button
            type="button"
            className="video-block__action"
            onClick={() => {
              setUrlDraft('');
              setUrlError('');
              patchProps({ mode: 'chooser', provider: null });
            }}
          >
            Back
          </button>
          <button type="button" className="video-block__action" onClick={handleRemove}>
            Remove
          </button>
        </div>
      </div>
    );
  }

  const errorActions = (
    <div className="video-block__chooser-actions">
      {canRetry ? (
        <button
          type="button"
          className="video-block__action video-block__action--primary"
          onClick={handleRetry}
        >
          Retry
        </button>
      ) : (
        <button
          type="button"
          className="video-block__action video-block__action--primary"
          onClick={chooseUpload}
        >
          Choose video
        </button>
      )}
      <button type="button" className="video-block__action" onClick={chooseEmbed}>
        Embed link instead
      </button>
      <button type="button" className="video-block__action" onClick={handleRemove}>
        Remove
      </button>
    </div>
  );

  // —— Hard upload error (no preview) ——
  if (hardError) {
    return (
      <div className="video-block video-block--error" role="alert" contentEditable={false}>
        <div className="video-block__error-title">Couldn’t add video</div>
        <div className="video-block__error-msg">
          {errorMessage || 'Something went wrong'}
        </div>
        {errorActions}
      </div>
    );
  }

  const showHandles = selected || hovered || draftWidth != null;
  const isFileVideo = !embedUrl && !!playSrc;

  return (
    <div
      className={[
        'video-block',
        selected ? 'video-block--selected' : '',
        draftWidth != null ? 'video-block--resizing' : '',
        isUploading ? 'video-block--uploading' : '',
        uploadFailed ? 'video-block--upload-failed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      contentEditable={false}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={frameRef}
        className="video-block__frame"
        style={{ width: `${widthPct}%` }}
        {...(dragListeners || {})}
        {...(dragAttributes || {})}
      >
        {embedUrl ? (
          <iframe
            className="video-block__iframe"
            src={embedUrl}
            title={providerLabel(provider)}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <DocVideoPlayer
            src={playSrc}
            disabled={isUploading || uploadFailed}
          />
        )}
        {isUploading && (
          <div
            className="video-block__upload-overlay"
            title="Uploading to cloud storage"
          >
            <div className="video-block__upload-label">
              Uploading
              {typeof uploadProgress === 'number' ? ` ${uploadProgress}%` : '…'}
            </div>
            <div className="video-block__upload-track">
              <div
                className="video-block__upload-bar"
                style={{
                  width: `${
                    typeof uploadProgress === 'number'
                      ? Math.max(4, uploadProgress)
                      : 12
                  }%`,
                }}
              />
            </div>
          </div>
        )}
        {uploadFailed && (
          <div
            className="video-block__fail-panel"
            role="alert"
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="video-block__fail-msg">
              {errorMessage || 'Upload failed'}
            </div>
            {errorActions}
          </div>
        )}
        {showHandles && !uploadFailed && (
          <>
            <button
              type="button"
              className="video-block__pin video-block__pin--tl"
              aria-label="Resize from top-left"
              onPointerDown={startResize('left')}
              onClick={stopPinClick}
            />
            <button
              type="button"
              className="video-block__pin video-block__pin--tr"
              aria-label="Resize from top-right"
              onPointerDown={startResize('right')}
              onClick={stopPinClick}
            />
            <button
              type="button"
              className="video-block__pin video-block__pin--bl"
              aria-label="Resize from bottom-left"
              onPointerDown={startResize('left')}
              onClick={stopPinClick}
            />
            <button
              type="button"
              className="video-block__pin video-block__pin--br"
              aria-label="Resize from bottom-right"
              onPointerDown={startResize('right')}
              onClick={stopPinClick}
            />
          </>
        )}
      </div>
      {(originalUrl || (isFileVideo && fileName)) && (
        <div className="video-block__caption">
          {originalUrl ? (
            <a
              href={originalUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {providerLabel(provider)} · open original
            </a>
          ) : (
            <span>{fileName}</span>
          )}
        </div>
      )}
    </div>
  );
};
